import { useState, useMemo } from "react";
import { useLingui } from "@lingui/react/macro";
import { Search, PencilLine, Plus, Columns3, ArrowUpDown } from "lucide-react";
import {
  IngredientFilterPanel,
  EMPTY_FILTERS,
  type IngredientFilters,
} from "./IngredientFilterPanel";
import { getRecipeIngredientColumns } from "./ingredient-columns";
import type { Recipe, RecipeIngredient } from "@/types/recipe";
import { ingredientLineName } from "@/types/recipe";
import { AddIngredientDialog, EditIngredientDialog } from "@/components/recipes/dialogs/AddIngredientDialog";
import { useRemoveIngredient, useRecipeIngredients, useCostSummary } from "@/hooks/recipes";
import type { CostedLine } from "@/lib/api/requests/recipes";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { toast } from "sonner";
import { DataTable } from "@/components/data-table";
import { Button } from "@/components/ui/button";

interface IngredientsTabProps {
  recipe: Recipe;
  onEdit?: () => void;
  /** Controls the "Add ingredient" affordances independent of the Edit button. */
  canAdd?: boolean;
}

export function IngredientsTab({ recipe, onEdit, canAdd = false }: IngredientsTabProps) {
  // `getRecipeIngredientColumns` builds its labels from `msg` descriptors, so
  // it takes the `i18n` instance rather than the macro's `t`.
  const { t, i18n } = useLingui();
  const [search, setSearch] = useState("");
  const [appliedFilters, setAppliedFilters] = useState<IngredientFilters>(EMPTY_FILTERS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState<RecipeIngredient | null>(null);
  const [removingIngredient, setRemovingIngredient] = useState<RecipeIngredient | null>(null);

  const removeIngredient = useRemoveIngredient();

  const { data: response, isLoading } = useRecipeIngredients(recipe.id);
  const { data: costSummary } = useCostSummary(recipe.id);

  const ingredients = useMemo(() => response?.data ?? [], [response]);

  // The rollup answers per ingredient row, so the table looks costs up by the
  // same id it renders by rather than trying to re-derive them here.
  const costByIngredientId = useMemo(() => {
    const map = new Map<number, CostedLine>();
    for (const line of costSummary?.lines ?? []) map.set(line.ingredientId, line);
    return map;
  }, [costSummary]);

  const availableUnits = useMemo(() => {
    const seen = new Set<string>();
    for (const ing of ingredients) {
      if (ing.unit) seen.add(ing.unit.toLowerCase());
    }
    return [...seen];
  }, [ingredients]);

  const availableAllergens = useMemo(() => {
    const seen = new Map<string, string>();
    for (const ing of ingredients) {
      for (const a of ing.material?.allergens ?? []) {
        if (!a?.allergen?.code) continue;
        if (!seen.has(a.allergen.code)) {
          seen.set(a.allergen.code, a.allergen.name);
        }
      }
    }
    return [...seen.entries()].map(([code, name]) => ({ code, name }));
  }, [ingredients]);

  const filtered = useMemo(() => {
    let result = ingredients;

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (ing) =>
          // Not `material?.name`: a sub-recipe line has no material, and
          // searching for the recipe it names has to find it.
          ingredientLineName(ing).toLowerCase().includes(q) ||
          ing.groupName?.toLowerCase().includes(q) ||
          ing.preparationNotes?.toLowerCase().includes(q),
      );
    }

    if (appliedFilters.structure.length > 0) {
      result = result.filter((ing) => {
        const type = ing.material?.type;
        if (appliedFilters.structure.includes("compound") && type === "COMPOUND") return true;
        if (appliedFilters.structure.includes("raw") && type === "RAW") return true;
        return false;
      });
    }

    if (appliedFilters.costMin !== "") {
      const min = parseFloat(appliedFilters.costMin);
      result = result.filter((ing) => (ing.material?.unitCost ?? 0) >= min);
    }
    if (appliedFilters.costMax !== "") {
      const max = parseFloat(appliedFilters.costMax);
      result = result.filter((ing) => (ing.material?.unitCost ?? Infinity) <= max);
    }

    if (appliedFilters.units.length > 0) {
      result = result.filter((ing) =>
        appliedFilters.units.includes((ing.unit ?? "").toLowerCase()),
      );
    }

    if (appliedFilters.allergenMode === "with") {
      result = result.filter((ing) => (ing.material?.allergens?.length ?? 0) > 0);
    } else if (appliedFilters.allergenMode === "without") {
      result = result.filter((ing) => (ing.material?.allergens?.length ?? 0) === 0);
    }

    if (appliedFilters.selectedAllergens.length > 0) {
      result = result.filter((ing) => {
        const codes = new Set(
          ing.material?.allergens?.map((a) => a?.allergen?.code).filter(Boolean) ?? [],
        );
        return appliedFilters.selectedAllergens.some((code) => codes.has(code));
      });
    }

    return result;
  }, [ingredients, search, appliedFilters]);

  const columns = useMemo(() => {
    const all = getRecipeIngredientColumns(i18n, {
      costByIngredientId,
      onEdit: setEditingIngredient,
      onRemove: setRemovingIngredient,
    });
    // Without `canAdd` the tab is read-only (Working mode, YC-33): no row menu
    // either, since its only entries are Edit and Remove.
    return canAdd ? all : all.filter((column) => column.id !== "actions");
  }, [i18n, costByIngredientId, canAdd]);

  return (
    <div className="flex flex-col gap-3">
      <AddIngredientDialog
        recipeId={recipe.id}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
      {editingIngredient && (
        <EditIngredientDialog
          recipeId={recipe.id}
          ingredient={editingIngredient}
          open={!!editingIngredient}
          onOpenChange={(v) => { if (!v) setEditingIngredient(null); }}
        />
      )}
      <ConfirmDeleteDialog
        open={!!removingIngredient}
        onOpenChange={(v) => { if (!v) setRemovingIngredient(null); }}
        title={t`Remove ingredient?`}
        description={t`This will remove ${ingredientLineName(removingIngredient ?? {}) || "this ingredient"} from the recipe.`}
        bullets={[t`This action cannot be undone`]}
        confirmLabel={t`Remove ingredient`}
        onConfirm={() => {
          if (!removingIngredient) return;
          removeIngredient.mutate(
            { id: recipe.id, ingredientId: removingIngredient.id },
            {
              onSuccess: () => {
                toast.success(t`Ingredient removed`);
                setRemovingIngredient(null);
              },
              onError: () => toast.error(t`Failed to remove ingredient`),
            },
          );
        }}
        isPending={removeIngredient.isPending}
      />

      {/* Section header */}
      <div className="flex items-center justify-between pl-2">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-medium leading-none text-(--color-text-primary)">
            {t`Ingredients`}
          </h2>
          <div className="flex w-10 items-center justify-center rounded-full bg-(--color-surface-muted) px-2 py-1">
            <span className="text-sm font-medium text-(--color-text-secondary)">
              {isLoading ? "—" : ingredients.length}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onEdit && (
            <Button variant="brand-outline" onClick={onEdit}>
              <PencilLine className="size-5" />
              {t`Edit`}
            </Button>
          )}
          {canAdd && (
            <Button onClick={() => setDialogOpen(true)} className="rounded-[10px]">
              <Plus className="size-5" />
              {t`Add ingredient`}
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col rounded-[10px] border border-(--color-border)">
        {/* Search + controls — always visible */}
        <div className="flex items-center justify-between border-b border-(--color-border) p-3">
          <div className="flex w-75 items-center gap-2 rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3 py-2 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]">
            <Search className="size-5 shrink-0 text-(--color-text-secondary)" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t`Search...`}
              className="min-w-0 flex-1 bg-transparent text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary)"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="brand-outline" size="sm" className="rounded-[8px]">
              <Columns3 className="size-4" />
              {t`Columns`}
            </Button>
            <Button variant="brand-outline" size="sm" className="rounded-[8px]">
              <ArrowUpDown className="size-4" />
              {t`Sort`}
            </Button>
            <IngredientFilterPanel
              filters={appliedFilters}
              onApply={setAppliedFilters}
              availableUnits={availableUnits}
              availableAllergens={availableAllergens}
            />
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="divide-y divide-(--gray-100)">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex gap-4 px-3 py-3">
                <div className="h-4 w-16 animate-pulse rounded bg-(--color-surface-muted)" />
                <div className="h-4 w-24 animate-pulse rounded bg-(--color-surface-muted)" />
                <div className="h-4 w-32 animate-pulse rounded bg-(--color-surface-muted)" />
                <div className="h-4 w-12 animate-pulse rounded bg-(--color-surface-muted)" />
              </div>
            ))}
          </div>
        ) : ingredients.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <p className="text-sm text-(--color-text-secondary)">{t`No ingredients added yet.`}</p>
            {canAdd && (
              <Button variant="link" onClick={() => setDialogOpen(true)} className="h-auto p-0 text-sm text-(--color-text-primary)">
                {t`Add ingredients`}
              </Button>
            )}
          </div>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={filtered}
              getRowId={(ing) => String(ing.id)}
              emptyMessage={t`No results match your search.`}
              className="[&_table]:min-w-250"
            />

            <div className="flex items-center justify-between border-t border-(--color-border) px-4 py-3">
              <p className="text-sm text-(--color-text-secondary)">
                {t`From 1–${filtered.length}`}
              </p>
              <p className="text-sm text-(--color-text-secondary)">{t`Page 1 of 1`}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
